// מיקום פרסום בדף הדמו: iframe במידות המדויקות של הבאנר מה-sandbox origin. אין מתיחה — אי התאמה מוצגת כהודעה.
import { useEffect, useRef, useState } from 'react';

export interface Slot { id: string; name: string; position: string; width: number; height: number; sort: number }
export interface DemoPackage { id: string; label: string; version: string; width: number | null; height: number | null; status: string; source: string; export_id: string | null; checksum: string; errors?: any[]; created_at: string }
export interface Assignment { id: string; slot_id: string; demo_package_id: string; package: DemoPackage; export: { id: string; kind: string; banner_revision: number; resolved_url: string | null; package_name: string } | null; frameUrl: string }

interface Props {
  slot: Slot;
  assignment: Assignment | null;
  packages: DemoPackage[];
  lazy: boolean;
  replayKey: number;
  scrollRoot: HTMLElement | null;
  onAssign: (slotId: string, pkgId: string) => Promise<void>;
  onUnassign: (assignmentId: string) => Promise<void>;
}

export default function DemoSlot({ slot, assignment, packages, lazy, replayKey, scrollRoot, onAssign, onUnassign }: Props) {
  const box = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(!lazy);
  const [key, setKey] = useState(0);
  const [msg, setMsg] = useState<string | null>(null);
  const [pick, setPick] = useState('');
  const [loadedAt, setLoadedAt] = useState<number | null>(null);

  // מצב lazy: ה-iframe נוצר רק כשהמיקום נכנס למסך
  useEffect(() => {
    if (!lazy) { setVisible(true); return; }
    setVisible(false);
    const el = box.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setVisible(true); return; }
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { setVisible(true); io.disconnect(); } }, { root: scrollRoot, threshold: 0.01 });
    io.observe(el);
    return () => io.disconnect();
  }, [lazy, scrollRoot, replayKey]);

  const choose = async (pkgId: string) => {
    setPick(pkgId); setMsg(null);
    if (!pkgId) return;
    const p = packages.find((x) => x.id === pkgId);
    if (p && (p.width !== slot.width || p.height !== slot.height)) {
      setMsg(`אי התאמת מידות: החבילה ${p.width ?? '?'}×${p.height ?? '?'} והמיקום ${slot.width}×${slot.height}. באנר אינו נמתח לגודל אחר.`);
      return;
    }
    try { await onAssign(slot.id, pkgId); setPick(''); } catch (e: any) { setMsg(e.message); }
  };

  return (
    <div className="dm-slot" data-position={slot.position}>
      <span className="dm-slot-label">פרסומת · {slot.name} · <span className="mono">{slot.width}×{slot.height}</span></span>
      <div ref={box} className="dm-slot-box" style={{ width: slot.width, height: slot.height }}>
        {assignment ? (
          visible ? (
            <iframe
              key={`${replayKey}-${key}`}
              title={`באנר ${assignment.package.label} ${slot.width}x${slot.height}`}
              src={assignment.frameUrl}
              width={slot.width}
              height={slot.height}
              style={{ width: slot.width, height: slot.height }}
              sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
              onLoad={() => setLoadedAt(Date.now())}
            />
          ) : <span className="dm-slot-msg">ממתין לכניסה למסך (lazy)</span>
        ) : <span className="dm-slot-msg">מיקום ריק</span>}
      </div>
      <div className="dm-slot-tools" dir="rtl">
        {assignment && <>
          <span className="small muted">{assignment.package.label}{assignment.package.version ? ` · ${assignment.package.version}` : ''}{assignment.export ? ` · r${assignment.export.banner_revision} · ${assignment.export.kind}` : ' · ZIP חיצוני'}</span>
          <button className="btn small" onClick={() => setKey((k) => k + 1)} aria-label={`טעינה מחדש של הבאנר ב${slot.name}`}>↻ טען מחדש</button>
          <button className="btn small danger" onClick={() => onUnassign(assignment.id).catch((e) => setMsg(e.message))} aria-label={`הסרת הבאנר מ${slot.name}`}>הסר</button>
          {loadedAt && <span className="small muted" aria-live="polite">נטען</span>}
        </>}
        <select aria-label={`שיבוץ חבילה ל${slot.name}`} value={pick} onChange={(e) => choose(e.target.value)} style={{ maxWidth: 200 }}>
          <option value="">{assignment ? 'החלפת חבילה…' : 'שיבוץ חבילה…'}</option>
          {packages.filter((p) => p.status === 'ready').map((p) => (
            <option key={p.id} value={p.id}>{p.width === slot.width && p.height === slot.height ? '✓ ' : '✗ '}{p.label} ({p.width}×{p.height})</option>
          ))}
        </select>
      </div>
      {msg && <div className="alert warn small" role="alert" style={{ maxWidth: Math.max(240, slot.width) }}>{msg}</div>}
    </div>
  );
}
